import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, brl, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { usePrintReady } from "@/lib/print";
import { METHOD_LABELS } from "@/components/PaymentDialog";
import { QRCodeSVG } from "qrcode.react";

const NF = { nfse: "NFS-e", nfe: "NF-e", nfce: "NFC-e" };
const css = (w) => `@page { size: ${w}mm auto; margin: ${w === 58 ? 2 : 3}mm; } @media print { .no-print { display: none !important; } body { background: #fff !important; } }
.cupom { width: ${w === 58 ? 54 : 74}mm; margin: 0 auto; font-family: "Courier New", monospace; font-size: 11px; color: #000; background: #fff; line-height: 1.35; }
.cupom hr { border: 0; border-top: 1px dashed #000; margin: 6px 0; } .cupom .r { display: flex; justify-content: space-between; gap: 6px; }`;

export default function ReceiptPrint({ kind }) {
  const { id } = useParams();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [w, setW] = useState(80);
  useEffect(() => {
    api.get("/erp/report-settings").then(({ data }) => setW(Number(data.thermal_width) === 58 ? 58 : 80)).catch(() => {});
  }, []);
  useEffect(() => {
    const url = kind === "sale" ? `/sales/${id}` : kind === "order" ? `/orders/${id}` : `/fiscal/invoices/${id}/print-data`;
    api.get(url).then(({ data }) => setD(data)).catch(() => setD({ error: true }));
  }, [id, kind]);
  usePrintReady(!d ? "loading" : d.error ? "error" : "ready", "Comprovante não encontrado");
  useEffect(() => {
    if (d && !d.error && new URLSearchParams(window.location.search).get("embed") === "1") document.body.style.background = "#fff";
  }, [d]);
  if (!d) return <div className="p-6 text-sm">Carregando…</div>;
  if (d.error) return <div className="p-6 text-sm">Não encontrado.</div>;
  const shop = d.shop || user?.shop || {};
  const lines = kind === "sale" ? d.items.map((i) => ({ description: i.description, qty: i.qty, unit_price: i.unit_price }))
    : kind === "order" ? ((d.budget && d.budget.items) || []).map((i) => ({ description: i.description, qty: i.quantity, unit_price: i.unit_price })) : d.lines;
  const total = kind === "sale" ? d.total : kind === "order" ? ((d.budget && d.budget.total) || 0) : d.amount;
  const custName = kind === "order" ? (d.customer?.name || d.customer_name) : d.customer_name;
  return (
    <div className="min-h-screen bg-white py-4">
      <style>{css(w)}</style>
      <div className="no-print text-center mb-3"><button data-testid="receipt-print-btn" onClick={() => window.print()} className="px-4 py-2 bg-black text-white rounded text-sm font-bold">Imprimir</button></div>
      <div className="cupom" data-testid="receipt-cupom">
        <div style={{ textAlign: "center", fontWeight: 700, fontSize: 13 }}>{shop.name || "Oficina"}</div>
        {shop.phone && <div style={{ textAlign: "center" }}>{shop.phone}</div>}
        <hr />
        <div style={{ textAlign: "center", fontWeight: 700 }}>
          {kind === "sale" ? `COMPROVANTE DE VENDA V${d.number}` : kind === "order" ? `COMPROVANTE OS #${d.order_number || ""}` : `${NF[d.kind]} ${d.number ? `Nº ${d.number}` : ""}`}
        </div>
        <div style={{ textAlign: "center" }}>{kind !== "invoice" ? "NÃO É DOCUMENTO FISCAL" : d.env !== "producao" ? "SEM VALOR FISCAL (teste)" : ""}</div>
        {kind === "invoice" && d.status === "cancelada" && <div style={{ textAlign: "center", fontWeight: 700 }}>*** NOTA CANCELADA ***</div>}
        <div>{fmtDateTime(d.created_at)}</div>
        {kind === "order" && d.vehicle?.plate && <div>Veículo: {d.vehicle.plate} {d.vehicle.model || ""}</div>}
        <div>Cliente: {custName || "Consumidor final"}</div>
        {kind !== "order" && d.customer_doc && <div>CPF/CNPJ: {d.customer_doc}</div>}
        <hr />
        {kind === "invoice" && d.access_key && <div style={{ wordBreak: "break-all", fontSize: 10 }}>Chave: {d.access_key}</div>}
        {lines.map((l, i) => (
          <div key={i}>
            <div>{l.description}</div>
            <div className="r"><span>{Number(l.qty)} x {brl(l.unit_price)}</span><span>{brl(Number(l.qty) * Number(l.unit_price))}</span></div>
          </div>
        ))}
        <hr />
        {kind === "sale" && d.discount_value > 0 && <><div className="r"><span>Subtotal</span><span>{brl(d.subtotal)}</span></div><div className="r"><span>Desconto</span><span>- {brl(d.discount_value)}</span></div></>}
        <div className="r" style={{ fontWeight: 700, fontSize: 13 }}><span>TOTAL</span><span>{brl(total)}</span></div>
        {kind === "sale" && d.payment && d.payment.methods.map((m, i) => <div key={i} className="r"><span>{METHOD_LABELS[m.method] || m.method}</span><span>{brl(m.amount)}</span></div>)}
        {kind === "sale" && d.status === "cancelada" && <div style={{ textAlign: "center", fontWeight: 700 }}>*** VENDA CANCELADA ***</div>}
        <hr />
        <div style={{ textAlign: "center" }}>Obrigado pela preferência!</div>
        {kind === "sale" && shop.slug && (
          <div data-testid="receipt-qr" style={{ textAlign: "center", marginTop: 6 }}>
            <QRCodeSVG value={`${window.location.origin}/o/${shop.slug}`} size={96} />
            <div style={{ fontSize: 10 }}>Agende e acompanhe seus serviços</div>
          </div>
        )}
      </div>
    </div>
  );
}
